import {useCallback, useEffect, useRef, useState} from 'react';
import {useInView} from './useInView';

interface UseAutoAdvanceOptions {
    /** Number of items to cycle through. Below 2 the timer never starts. */
    count: number;
    /** How long each item stays active before the next one takes over. */
    intervalMs: number;
    /** Fraction of the container that must be visible before counting starts. */
    threshold?: number;
}

interface UseAutoAdvanceResult {
    activeIndex: number;
    /**
     * Selects an item and restarts the countdown. Pass `-1` to stop the cycle
     * entirely (used when a component lets the user collapse everything).
     */
    select: (index: number) => void;
    /**
     * Increments on every advance and every {@link select}. Use it as the `key` of a
     * CSS-animated progress indicator so the animation restarts in step with the timer.
     */
    cycle: number;
    /** Whether a countdown is currently in flight — i.e. a progress bar should show. */
    armed: boolean;
    /** True while held by hover; feed it to `animationPlayState` to freeze the indicator. */
    paused: boolean;
    /** Attach to the element whose visibility starts the cycle and whose hover pauses it. */
    containerProps: {
        ref: (node?: Element | null) => void;
        onMouseEnter: () => void;
        onMouseLeave: () => void;
    };
}

/**
 * Drives a self-advancing carousel/accordion: the active index steps forward every
 * `intervalMs` and wraps at the end.
 *
 * Three behaviours worth knowing:
 *
 * - **Counting starts on entry, not on mount.** A section far below the fold would
 *   otherwise have burned through several items before the reader ever saw it.
 * - **Hover pauses rather than restarts.** The unspent part of the interval is kept
 *   in a ref, so leaving the element resumes from where it stopped. Pair this with
 *   `animationPlayState: paused` on the visual indicator to keep the two in sync.
 * - **Manual selection resets the countdown**, so an item the reader just picked
 *   always gets its full interval.
 *
 * Timing is driven by `setTimeout` (not a per-frame loop) and the visual progress is
 * expected to be a CSS animation, so an idle cycle costs one render per interval.
 */
export function useAutoAdvance({count, intervalMs, threshold = 0.35}: UseAutoAdvanceOptions): UseAutoAdvanceResult {
    const [activeIndex, setActiveIndex] = useState(0);
    const [cycle, setCycle] = useState(0);
    const [paused, setPaused] = useState(false);
    const [containerRef, inView] = useInView({triggerOnce: true, threshold});

    const armed = inView && activeIndex >= 0 && count > 1;

    // Time left on the current item, so a pause is resumable.
    const remainingRef = useRef(intervalMs);
    const startedAtRef = useRef(0);

    useEffect(() => {
        remainingRef.current = intervalMs;
    }, [cycle, activeIndex, intervalMs]);

    useEffect(() => {
        if (!armed) return undefined;

        if (paused) {
            remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
            return undefined;
        }

        startedAtRef.current = Date.now();
        const timer = setTimeout(() => {
            setActiveIndex((i) => (i + 1) % count);
            setCycle((c) => c + 1);
        }, remainingRef.current);

        return () => clearTimeout(timer);
        // `cycle` is a dependency so a manual selection restarts the countdown.
    }, [armed, paused, cycle, activeIndex, count]);

    const select = useCallback((index: number) => {
        setActiveIndex(index);
        setCycle((c) => c + 1);
    }, []);

    return {
        activeIndex,
        select,
        cycle,
        armed,
        paused,
        containerProps: {
            ref: containerRef,
            onMouseEnter: () => setPaused(true),
            onMouseLeave: () => setPaused(false)
        }
    };
}
